import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
  try {
    if (!branchesData || !Array.isArray(branchesData) || !branchId) return 'N/A';
    const branch = branchesData.find(b => parseInt(b?.id) === parseInt(branchId));
    return branch ? `${branch.name || ''} (${branch.code || ''})`.trim() : 'N/A';
  } catch (error) {
    console.error('Error getting branch name:', error);
    return 'N/A';
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

// Generate export data for units
export const generateUnitsExportData = (units, branchesData) => {
  return units.map((unit, index) => ({
    '#': index + 1,
    'ID': unit.id || '',
    'Date': formatDate(unit.created_at),
    'Unit Name': unit.name || '',
    'Short Name': unit.short_name || '',
    'Status': unit.status === 1 ? 'Active' : 'Inactive',
    'Branch': getBranchName(unit.branch_id, branchesData),
    'Branch ID': unit.branch_id || '',
    'Created Date': formatDate(unit.created_at),
    'Created At': unit.created_at || '',
    'Updated Date': formatDate(unit.updated_at),
    'Updated At': unit.updated_at || ''
  }));
};

// Excel export for Units (with all attributes)
export const exportToExcel = (units, branchesData, filters = {}) => {
  try {
    // Generate export data
    const exportData = generateUnitsExportData(units, branchesData);
    
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Auto-size columns
    const maxWidth = exportData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Units');
    
    // Generate Excel file and download
    const fileName = `units_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    return true;
  } catch (error) {
    console.error('Units Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// PDF export for Units - Landscape mode (manual table implementation)
export const exportToPDF = (units, branchesData, filters = {}) => {
  try {
    // Create PDF in landscape mode
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Set document properties
    doc.setProperties({
      title: 'Units Report',
      subject: 'Units Export',
      author: companyInfo.name,
      creator: companyInfo.name
    });
    
    // Page dimensions (landscape)
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    
    // Column definitions - Only: Date, Unit Name, Short Name, Branch
    const columns = [
      { key: 'Date', header: 'Date', width: 30 },
      { key: 'Unit Name', header: 'Unit Name', width: 70 },
      { key: 'Short Name', header: 'Short Name', width: 30 },
      { key: 'Branch', header: 'Branch', width: 70 }
    ];
    
    // Generate export data for PDF (only needed columns)
    const exportData = units.map((unit, index) => ({
      'Date': formatDate(unit.created_at),
      'Unit Name': unit.name || '',
      'Short Name': unit.short_name || '',
      'Branch': getBranchName(unit.branch_id, branchesData)
    }));
    
    // Calculate table width
    const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);
    const availableWidth = pageWidth - (2 * margin);
    
    // Scale columns if needed
    let scaleFactor = 1;
    if (totalTableWidth > availableWidth) {
      scaleFactor = availableWidth / totalTableWidth;
      columns.forEach(col => {
        col.width *= scaleFactor;
      });
    }
    
    const tableStartY = 60;
    const headerHeight = 10;
    const rowHeight = 10;
    
    let currentY = tableStartY;
    let currentPage = 1;
    
    // Function to draw header
    const drawHeader = () => {
      // Title
      doc.setFontSize(20);
      doc.setTextColor(25, 118, 210);
      doc.text('UNITS REPORT', pageWidth / 2, 20, { align: 'center' });
      
      // Company info
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(companyInfo.name, margin, 30);
      
      // Generation info
      const dateTime = new Date().toLocaleString();
      doc.text(`Generated: ${dateTime}`, pageWidth - margin, 30, { align: 'right' });
      
      // Record count
      doc.text(`Total Units: ${units.length}`, margin, 40);
      
      // Page info
      doc.text(`Page: ${currentPage}`, pageWidth - margin, 40, { align: 'right' });
      
      // Draw table header
      doc.setFillColor(25, 118, 210);
      doc.rect(margin, currentY, totalTableWidth, headerHeight, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      let xPos = margin;
      columns.forEach(column => {
        const textX = xPos + 2;
        const textY = currentY + (headerHeight / 2) + 3;
        
        // Center the header text
        const textWidth = doc.getTextWidth(column.header);
        const centerX = xPos + (column.width / 2) - (textWidth / 2);
        
        if (centerX > xPos) {
          doc.text(column.header, centerX, textY);
        } else {
          doc.text(column.header, textX, textY);
        }
        
        xPos += column.width;
      });
      
      currentY += headerHeight;
    };
    
    // Function to draw a row
    const drawRow = (row, rowIndex) => {
      // Alternate row background
      if (rowIndex % 2 === 0) {
        doc.setFillColor(248, 248, 248);
        doc.rect(margin, currentY, totalTableWidth, rowHeight, 'F');
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      let xPos = margin;
      
      columns.forEach(column => {
        const textX = xPos + 2;
        const textY = currentY + (rowHeight / 2) + 3;
        let cellText = String(row[column.key] || 'N/A');
        
        // Truncate text if too long
        const maxCellWidth = column.width - 4;
        let textWidth = doc.getTextWidth(cellText);
        
        while (textWidth > maxCellWidth && cellText.length > 3) {
          cellText = cellText.substring(0, cellText.length - 4) + '...';
          textWidth = doc.getTextWidth(cellText);
        }
        
        // Draw text
        doc.text(cellText, textX, textY);
        xPos += column.width;
      });
      
      currentY += rowHeight;
    };
    
    // Draw first page header
    drawHeader();
    
    // Draw rows
    exportData.forEach((row, rowIndex) => {
      // Check for page break
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage('landscape');
        currentPage++;
        currentY = margin;
        drawHeader();
      }
      
      drawRow(row, rowIndex);
    });
    
    // Add a border around the entire table
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, tableStartY, totalTableWidth, currentY - tableStartY);
    
    // Save PDF
    const fileName = `units_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return true;
  } catch (error) {
    console.error('Units PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// CSV export for Units (with all attributes)
export const exportToCSV = (units, branchesData) => {
  try {
    // Generate export data with all attributes
    const exportData = generateUnitsExportData(units, branchesData);
    
    // Prepare headers (all attributes)
    const headers = [
      '#', 'ID', 'Date', 'Unit Name', 'Short Name', 'Status', 
      'Branch', 'Branch ID', 'Created Date', 'Created At', 
      'Updated Date', 'Updated At'
    ];
    
    // Prepare CSV rows
    const csvRows = exportData.map(unit => {
      return headers.map(header => {
        const value = unit[header] || '';
        // Escape quotes and wrap in quotes if contains comma or quotes
        const stringValue = String(value).replace(/"/g, '""');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue}"`;
        }
        return stringValue;
      });
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download CSV file
    const fileName = `units_${new Date().toISOString().split('T')[0]}.csv`;
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Units CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};